'use client';
import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import AppImage from '@/components/ui/AppImage';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


export default function CaseStudyDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));

    // Sticky CTA on scroll
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const caseStudyData: Record<string, any> = {
    'vegas-nights-campaign': {
      title: 'Vegas Nights Campaign',
      category: 'Hospitality',
      client: 'Luxury Hotel Group',
      year: '2025',
      services: ['Production', 'Creative Direction', 'Marketing'],
      heroImage: "https://images.unsplash.com/photo-1698350366977-e7f15bf8b685",
      heroImageAlt: 'Illuminated Las Vegas strip at night with city lights and bustling crowds',
      resultsMetrics: [
      { label: 'Booking Increase', value: '45%' },
      { label: 'Social Impressions', value: '2M+' },
      { label: 'Booking Value', value: '+15%' }],

      problem: 'The luxury hotel group faced declining bookings during the competitive holiday season. Despite having premium properties, they struggled to differentiate themselves in a crowded market. Their existing marketing lacked emotional resonance and failed to capture the aspirational experience they offered.',
      strategy: 'We developed a cinematic storytelling approach that positioned the hotels as the gateway to unforgettable Vegas experiences. The strategy centered on creating desire through visual storytelling, targeting high-net-worth travelers with precision digital campaigns, and leveraging social proof from influencer partnerships.',
      execution: [
      'Produced 3 cinematic video spots showcasing luxury experiences',
      'Launched multi-channel paid media campaign (Meta, Google, YouTube)',
      'Partnered with 12 luxury lifestyle influencers for authentic content',
      'Created interactive landing pages with immersive 360° hotel tours',
      'Implemented retargeting strategy with dynamic creative optimization'],

      results: 'The campaign exceeded all benchmarks, driving a 45% increase in direct bookings during the holiday season. Social media impressions surpassed 2 million, with engagement rates 3x higher than industry average. Most importantly, average booking value increased by 15%, indicating we attracted the premium audience segment.',
      nextSteps: {
        description: 'To scale this success, we recommend expanding the campaign to year-round execution with seasonal creative refreshes. The proven creative framework can be adapted for other properties and markets.',
        recommendedPlan: 'Growth Partner',
        planReason: 'Ongoing campaign management, creative production, and optimization require dedicated monthly capacity (80h+). Growth Partner provides the strategic oversight and execution bandwidth needed to maintain momentum.'
      },
      gallery: [
      { src: 'https://images.unsplash.com/photo-1628840803162-6824ada8c7b0', alt: 'Las Vegas strip at night with city lights' },
      { src: "https://images.unsplash.com/photo-1721323381664-c0a51fc1cf78", alt: 'Luxury hotel lobby with modern design' },
      { src: "https://images.unsplash.com/photo-1507049807929-b573086df126", alt: 'Hotel pool deck at sunset with city views' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e7795d03-1767702212071.png", alt: 'Elegant hotel suite with floor-to-ceiling windows' }]

    },
    'high-roller-launch': {
      title: 'High Roller Launch',
      category: 'Entertainment',
      client: 'Premium Entertainment Resort',
      year: '2025',
      services: ['Production', 'Events', 'Marketing'],
      heroImage: "https://images.unsplash.com/photo-1571536186674-599e2a8c0666",
      heroImageAlt: 'Luxury venue interior with chandeliers and entertainment floor',
      resultsMetrics: [
      { label: 'Opening Revenue', value: '$5M+' },
      { label: 'VIP Memberships', value: '200+' },
      { label: 'Media Features', value: '15+' }],

      problem: 'The resort needed to launch a new VIP entertainment experience in a market saturated with premium offerings. Traditional advertising wouldn\'t reach their ultra-high-net-worth target audience, and they needed to create exclusivity and demand before opening day.',
      strategy: 'We designed an invitation-only launch strategy that leveraged scarcity and social proof. The approach focused on creating FOMO through exclusive previews, partnering with luxury lifestyle brands, and generating earned media through strategic PR placements.',
      execution: [
      'Curated guest list of 500 high-net-worth individuals and influencers',
      'Produced immersive launch event with live entertainment and live demos',
      'Secured partnerships with luxury brands (watches, spirits, automotive)',
      'Executed targeted PR campaign resulting in 15+ media placements',
      'Created VIP membership program with tiered benefits and exclusivity'],

      results: 'The launch exceeded revenue projections by 40%, generating over $5M in opening weekend entertainment revenue. We sold 200+ VIP memberships at $10k+ each before opening night. The event was featured in major entertainment and luxury lifestyle publications, establishing the venue as the premier premium entertainment destination.',
      nextSteps: {
        description: 'Maintain momentum with ongoing VIP member events, targeted acquisition campaigns, and content marketing to attract new VIP clientele. The foundation is set for sustained growth.',
        recommendedPlan: 'Execution Coverage',
        planReason: 'Monthly event production, member communications, and targeted campaigns fit within 40h/month. Execution Coverage provides the consistent execution needed without full Growth Partner investment.'
      },
      gallery: [
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9496dc2-1766265676283.png", alt: 'Luxury venue interior with elegant chandeliers' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_18f195ff6-1773451488614.png", alt: 'Premium venue detail with refined decor' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1b39c733d-1767426413289.png", alt: 'VIP lounge area with leather seating' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5f36ad3-1766055675276.png", alt: 'Entertainment floor with ambient lighting' }]

    },
    'desert-dreams': {
      title: 'Desert Dreams',
      category: 'Real Estate',
      client: 'Luxury Development Group',
      year: '2024',
      services: ['Photography', 'Creative Direction', 'Marketing'],
      heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_146a75e5c-1772202677801.png",
      heroImageAlt: 'Modern luxury home exterior with desert landscape at sunset',
      resultsMetrics: [
      { label: 'Units Sold', value: '85%' },
      { label: 'Total Sales', value: '$120M' },
      { label: 'Time to Sell', value: '6 months' }],

      problem: 'A new luxury residential development struggled to stand out in the competitive Las Vegas real estate market. Despite premium properties, initial marketing efforts failed to convey the unique desert lifestyle and architectural excellence that justified the price point.',
      strategy: 'We repositioned the development as a lifestyle brand, not just real estate. The strategy emphasized the intersection of modern architecture and natural desert beauty, targeting affluent buyers seeking a retreat from urban density.',
      execution: [
      'Produced stunning architectural photography showcasing design details',
      'Created lifestyle video content featuring desert living experiences',
      'Launched targeted digital campaigns to affluent buyer segments',
      'Developed immersive website with virtual tours and neighborhood guides',
      'Executed PR strategy resulting in features in design and lifestyle publications'],

      results: 'The campaign transformed sales velocity, moving from 20% to 85% sold in just 6 months. Total sales reached $120M, exceeding projections by 30%. The marketing campaign won multiple industry awards and established the developer as a premium brand.',
      nextSteps: {
        description: 'Apply this proven framework to future developments. The content library and brand positioning can be adapted for new properties, reducing time-to-market and marketing costs.',
        recommendedPlan: 'Strategy Sprint',
        planReason: 'For new developments, start with a Strategy Sprint to adapt the framework to the specific property and market. Then scale execution based on project scope.'
      },
      gallery: [
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_10ab0febe-1772295517111.png", alt: 'Modern luxury home with desert landscape' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff774c7d-1767672529551.png", alt: 'Contemporary home interior with floor-to-ceiling windows' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_15fbf6814-1767599799066.png", alt: 'Outdoor living space with pool and mountain views' },
      { src: "https://img.rocket.new/generatedImages/rocket_gen_img_16c209ab2-1766863294465.png", alt: 'Master bedroom with panoramic desert views' }]

    }
  };

  const caseStudy = caseStudyData[slug] || null;

  if (!caseStudy) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background text-foreground pt-24">
          <section className="py-24 px-6">
            <div className="max-w-[1600px] mx-auto text-center">
              <p className="text-micro uppercase tracking-ultra-wide text-muted-foreground mb-4">Case Studies</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">New Case Studies in Development</h1>
              <p className="text-body text-muted-foreground max-w-xl mx-auto mb-10">
                We&apos;re documenting recent client work and will be publishing detailed case studies shortly. Check back soon.
              </p>
              <Link href="/case-studies">
                <Button variant="secondary" size="lg">← Back to Case Studies</Button>
              </Link>
            </div>
          </section>
          <Footer />
        </main>
      </>);

  }

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % caseStudy.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + caseStudy.gallery.length) % caseStudy.gallery.length);
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-8">
              <Badge variant="primary">{caseStudy.category}</Badge>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-12 reveal">
              {caseStudy.title}
            </h1>
            
            {/* Results Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 reveal">
              {caseStudy.resultsMetrics.map((metric: any, index: number) =>
              <div key={index} className="border border-border p-8 bg-surface-1">
                  <span className="text-5xl md:text-6xl font-bold text-primary block mb-2">
                    {metric.value}
                  </span>
                  <span className="text-micro uppercase tracking-ultra-wide text-muted-foreground">
                    {metric.label}
                  </span>
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 reveal">
              <Link href="/book">
                <Button variant="primary" size="lg" className="border-glow w-full sm:w-auto">
                  Book Consultation
                </Button>
              </Link>
              <Link href="/pricing">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  View Pricing
                </Button>
              </Link>
            </div>

            {/* Project Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 reveal">
              <div>
                <span className="text-micro uppercase tracking-ultra-wide text-muted-foreground block mb-2">Client</span>
                <p className="text-body">{caseStudy.client}</p>
              </div>
              <div>
                <span className="text-micro uppercase tracking-ultra-wide text-muted-foreground block mb-2">Year</span>
                <p className="text-body">{caseStudy.year}</p>
              </div>
              <div>
                <span className="text-micro uppercase tracking-ultra-wide text-muted-foreground block mb-2">Services</span>
                <p className="text-body">{caseStudy.services.join(', ')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Image */}
        <section className="px-6 py-12">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal aspect-video overflow-hidden border border-border cursor-pointer" onClick={() => openLightbox(0)}>
              <AppImage
                src={caseStudy.heroImage}
                alt={caseStudy.heroImageAlt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-slower" />
              
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl">
              <div className="reveal mb-8">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">01 — The Problem</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">The Challenge</h2>
              </div>
              <div className="reveal">
                <p className="text-lg text-muted-foreground leading-relaxed">{caseStudy.problem}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Strategy Section */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl">
              <div className="reveal mb-8">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">02 — The Strategy</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Approach</h2>
              </div>
              <div className="reveal">
                <p className="text-lg text-muted-foreground leading-relaxed">{caseStudy.strategy}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Execution Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl">
              <div className="reveal mb-8">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">03 — The Execution</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">What We Shipped</h2>
              </div>
              <div className="reveal space-y-4">
                {caseStudy.execution.map((item: string, index: number) =>
                <div key={index} className="flex items-start gap-4 p-6 border border-border hover:border-primary transition-colors">
                    <span className="text-primary font-bold text-xl flex-shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p className="text-body">{item}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl">
              <div className="reveal mb-8">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">04 — The Results</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Impact & Outcomes</h2>
              </div>
              <div className="reveal">
                <p className="text-lg text-muted-foreground leading-relaxed">{caseStudy.results}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-12">
              <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Gallery</span>
              <h2 className="text-4xl md:text-5xl font-bold">Visual Story</h2>
            </div>
            
            {/* Carousel */}
            <div className="reveal relative">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {caseStudy.gallery.map((image: any, index: number) =>
                <div
                  key={index}
                  className="aspect-square overflow-hidden border border-border cursor-pointer group"
                  onClick={() => openLightbox(index)}>
                  
                    <AppImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-slower" />
                  
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl">
              <div className="reveal mb-8">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">05 — Next Steps</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">How We'd Scale This</h2>
              </div>
              <div className="reveal mb-12">
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  {caseStudy.nextSteps.description}
                </p>
              </div>

              {/* Plan Recommendation */}
              <div className="reveal border border-primary p-8 bg-primary-muted">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-2">Recommended Plan</span>
                    <h3 className="text-3xl font-bold">{caseStudy.nextSteps.recommendedPlan}</h3>
                  </div>
                  <Badge variant="primary">Best Fit</Badge>
                </div>
                <p className="text-body-sm text-muted-foreground mb-6">
                  {caseStudy.nextSteps.planReason}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link href="/pricing">
                    <Button variant="primary" size="lg" className="border-glow w-full sm:w-auto">
                      View Plan Details
                    </Button>
                  </Link>
                  <Link href="/book">
                    <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                      Book Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1600px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Ready for Similar Results?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Let's discuss how we can help you achieve your goals with a proven, results-driven approach.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <Button variant="primary" size="lg" className="border-glow w-full sm:w-auto">
                    Book Consultation
                  </Button>
                </Link>
                <Link href="/case-studies">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                    View More Work
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Sticky CTA */}
      {showStickyCTA &&
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">{caseStudy.title}</p>
              <p className="text-body-sm text-muted-foreground">Want similar results?</p>
            </div>
            <Link href="/book">
              <Button variant="primary" size="lg" className="border-glow">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      }

      {/* Lightbox Modal */}
      <Modal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        size="xl">
        
        <div className="relative">
          <AppImage
            src={caseStudy.gallery[currentImageIndex]?.src}
            alt={caseStudy.gallery[currentImageIndex]?.alt}
            className="w-full h-auto max-h-[80vh] object-contain" />
          
          
          {/* Navigation Arrows */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-background/80 backdrop-blur-sm border border-border hover:border-primary transition-colors">
            
            <Icon name="ChevronLeftIcon" size={24} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-background/80 backdrop-blur-sm border border-border hover:border-primary transition-colors">
            
            <Icon name="ChevronRightIcon" size={24} />
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-background/80 backdrop-blur-sm border border-border">
            <span className="text-micro uppercase tracking-widest">
              {currentImageIndex + 1} / {caseStudy.gallery.length}
            </span>
          </div>
        </div>
      </Modal>

      <Footer />
    </>);


}